import ScrollModal from "@/components/common/scroll-modal";
import TextField from "@/components/common/text-field";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import {
  Button,
  Heading,
  Icon,
  Input,
  Radiobutton,
  GroupTextField,
  GroupTextFieldRow,
} from "@elaniintc/goes-design-system";
import TextFieldID from "@/pages/digital-files/entity/components/TextFieldID";
import SwitchField from "@/workflow/components/configurations/form/components/SwitchField";
import InfoMessage from "@/workflow/components/configurations/form/components/InfoMessage";
import type { EntityField } from "../../types";
import { useConfigModal } from "../hooks/useConfigModal";
import { useState } from "react";
import { DataCenterField } from "../components/DataCenterField";
import { Renderify } from "renderify-js";
import { maskitoNumberOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";

interface NumericFieldConfigModalProps {
  isOpen: boolean;
  isCreateMode?: boolean;
  formData?: EntityField;
  onSave: (data: EntityField) => void;
  onClose: () => void;
}

export const NumericFieldConfigModal = ({
  isOpen,
  isCreateMode = true,
  formData,
  onSave,
  onClose,
}: NumericFieldConfigModalProps) => {
  const [localListNewItem, setLocalListNewItem] = useState("");
  const {
    fieldKey,
    fieldName,
    fieldNameError,
    defaultValue,
    localListItems,
    hasDropdown,
    buttonPosition,
    valuesSource,
    placeholder,
    decimals,
    valuesColumnName,
    valuesTableName,
    setFieldKey,
    setFieldName,
    setDefaultValue,
    setLocalListItems,
    setHasDropdown,
    setButtonPosition,
    setValuesSource,
    setPlaceholder,
    setDecimals,
    setDataCenterValue,
    getConfigData,
    isValid,
    isNew,

    isArray,
    setIsArray,
    allowedQuantityOfValues,
    setAllowedQuantityOfValues,
  } = useConfigModal("number", formData);

  const maxNumberOfValuesMask = maskitoNumberOptionsGenerator({
    min: 1,
    minimumFractionDigits: 0,
  });
  const allowedQuantityOfValuesMask = useMaskito({
    options: maxNumberOfValuesMask,
  });

  const handleSave = () => {
    const values = getConfigData();
    onSave(values);
    onClose();
  };

  const handleAddLocalItem = () => {
    const v = localListNewItem.trim();
    if (!v) return;
    setLocalListItems((prev) => [...prev, v]);
    setLocalListNewItem("");
  };

  const handleRemoveLocalItem = (index: number) => {
    setLocalListItems((prev) => prev.filter((_, i) => i !== index));
  };

  let decimalsLabel = "";
  if (decimals === "auto") {
    decimalsLabel = "Automático";
  } else if (decimals === "1") {
    decimalsLabel = "1 decimal obligatorio";
  } else if (decimals === "2") {
    decimalsLabel = "2 decimales obligatorios";
  } else {
    decimalsLabel = "3 decimales obligatorios";
  }

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col gap-0 text-size-body-sm"
      header={
        <div className="py-3 px-8">
          <div className="flex items-center justify-between w-full">
            <Heading variant="h5">Campo numérico</Heading>
            <Button
              variant="quiet"
              size="icon"
              aria-label="Cerrar"
              onClick={onClose}
              className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="px-8 py-3">
          <div className="flex gap-3">
            <Button variant="tertiary" className="flex-1" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="default"
              className="flex-1"
              disabled={!isValid}
              onClick={handleSave}
            >
              Guardar
            </Button>
          </div>
        </div>
      }
    >
      <div className="p-8 flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Detalles
          </Heading>

          <TextFieldID
            label="Nombre del campo"
            required
            value={fieldName}
            onChange={setFieldName}
            keyValue={fieldKey}
            onKeyChange={setFieldKey}
            canChangeKey={isCreateMode || isNew}
            error={fieldNameError}
          />
        </div>

        <div className="border-t border-neutral-quiet" />

        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Opciones
          </Heading>

          <SwitchField
            id="numeric-field-dropdown"
            label="¿Agregar botón dropdown al campo?"
            required
            checked={hasDropdown}
            onChange={setHasDropdown}
            isTrue="Sí"
            isFalse="No"
          />

          {hasDropdown && (
            <>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-body-loud text-sm leading-2xl">
                  Posición del botón{" "}
                  <span className="text-sm text-danger">*</span>
                </label>
                <DropdownList
                  options={[
                    { value: "left", label: "Izquierda" },
                    { value: "right", label: "Derecha" },
                  ]}
                  placeholder="Seleccionar"
                  selectedValue={{
                    value: buttonPosition,
                    label: buttonPosition === "left" ? "Izquierda" : "Derecha",
                  }}
                  toggleSelection={(item) => {
                    if (item.value === "left" || item.value === "right") {
                      setButtonPosition(item.value);
                    }
                  }}
                  widthClassName="w-full"
                  scrollClassName="h-auto"
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="font-semibold text-body-loud text-sm leading-2xl">
                  ¿De donde provienen los valores?{" "}
                  <span className="text-sm text-danger">*</span>
                </div>
                <div className="flex flex-col gap-2 mt-1">
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="numeric-field-source-database"
                      name="numeric-field-source"
                      value="database"
                      checked={valuesSource === "database"}
                      onChange={() => setValuesSource("database")}
                    />
                    <label
                      htmlFor="numeric-field-source-database"
                      className="text-body text-sm"
                    >
                      Desde centro de datos
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="numeric-field-source-manual"
                      name="numeric-field-source"
                      value="manual"
                      checked={valuesSource === "manual"}
                      onChange={() => setValuesSource("manual")}
                    />
                    <label
                      htmlFor="numeric-field-source-manual"
                      className="text-body text-sm"
                    >
                      Locales
                    </label>
                  </div>
                </div>
              </div>

              {valuesSource === "manual" && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-end group">
                    <div className="w-full">
                      <TextField
                        placeholder="Añade un nuevo item"
                        className="rounded-tr-none rounded-br-none border-r-0"
                        label="Lista local"
                        required
                        value={localListNewItem}
                        onChange={(e) => setLocalListNewItem(e.target.value)}
                      />
                    </div>
                    <Button
                      className="h-11 rounded-bl-none rounded-tl-none border-l-0 group-hover:border-neutral-loud group-focus-within:border-contextual-action! group-hover:bg-neutral-object-accent-silent group-focus-within:bg-neutral-object-accent-quiet!"
                      variant="quiet"
                      type="button"
                      onClick={handleAddLocalItem}
                      aria-label="Agregar item"
                    >
                      <Icon name="plus" type="far" />
                    </Button>
                  </div>

                  <InfoMessage>
                    El primer valor de la lista será el pre-seleccionado por
                    default
                  </InfoMessage>

                  {localListItems.length > 0 && (
                    <div className="w-full rounded-lg border border-neutral bg-neutral-object shadow-sm p-1">
                      <div className="flex flex-col">
                        {localListItems.map((item, index) => (
                          <div
                            key={`${item}-${index}`}
                            className="px-3 py-2 flex items-center gap-3"
                          >
                            <div className="flex-1 text-body text-sm truncate">
                              {item}
                            </div>
                            <Button
                              variant="quiet"
                              size="icon"
                              type="button"
                              aria-label="Eliminar"
                              onClick={() => handleRemoveLocalItem(index)}
                              className="w-8 h-8"
                            >
                              <Icon type="far" name="times" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {valuesSource === "database" && (
                <DataCenterField
                  required
                  fieldKey="valueDatacenter"
                  selectedTable={valuesTableName}
                  selectedColumn={valuesColumnName}
                  onChange={setDataCenterValue}
                />
              )}
            </>
          )}
        </div>

        <div className="border-t border-neutral-quiet" />

        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Configuración
          </Heading>

          <TextField
            label="Placeholder del campo"
            required
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            placeholder="Escribe un placeholder para el campo"
          />

          <div className="flex flex-col gap-1">
            <label
              htmlFor="decimalsOption"
              className="font-semibold text-body-loud text-sm leading-2xl"
            >
              Decimales <span className="text-sm text-danger">*</span>
            </label>
            <DropdownList
              id="decimalsOption"
              options={[
                { value: "auto", label: "Automático" },
                { value: "1", label: "1 decimal obligatorio" },
                { value: "2", label: "2 decimales obligatorios" },
                { value: "3", label: "3 decimales obligatorios" },
              ]}
              placeholder="Seleccionar"
              selectedValue={{
                value: decimals,
                label: decimalsLabel,
              }}
              toggleSelection={(item) => {
                if (
                  item.value === "auto" ||
                  item.value === "1" ||
                  item.value === "2" ||
                  item.value === "3"
                ) {
                  setDecimals(item.value);
                }
              }}
              widthClassName="w-full"
              scrollClassName="h-auto"
            />
            <div className="text-xs leading-xl text-body-quiet mt-1">
              Si hay un número fijo de decimales, estos siempre aparecerán en el
              campo.
            </div>
          </div>

          <div>
            <GroupTextField
              label="Valor por defecto"
              labelHelperText="Opcional"
            >
              <GroupTextFieldRow className="items-center">
                <Input
                  value={defaultValue}
                  onChange={(e) => setDefaultValue(e.target.value)}
                  placeholder="Escribe un valor por defecto"
                  className="w-full"
                  type="number"
                />
              </GroupTextFieldRow>
            </GroupTextField>

            <InfoMessage className="mt-1">
              Si está vacío no se aplicará ningún valor por defecto.
            </InfoMessage>
          </div>
        </div>

        <SwitchField
          label="¿Agregar múltiples valores?"
          isTrue="Sí, permitir valores adicionales"
          checked={isArray}
          onChange={(checked) => {
            setIsArray(checked);
            if (!checked) {
              setAllowedQuantityOfValues("");
            }
          }}
          disabled={!(isCreateMode || isNew)}
          required
        />

        <Renderify condition={isArray}>
          <div>
            <TextField
              label="Cantidad máxima de items"
              placeholder="Escribe un número"
              ref={allowedQuantityOfValuesMask}
              value={allowedQuantityOfValues}
              onChange={(e) => setAllowedQuantityOfValues(e.target.value || "")}
              disabled={!(isCreateMode || isNew)}
            />
            <InfoMessage>
              Si se deja vacío, no se aplicará límite de items
            </InfoMessage>
          </div>
        </Renderify>
      </div>
    </ScrollModal>
  );
};
